import { useCreateFolder } from "@src/components/board/mutations/create-folder";
import { useDeleteFolder } from "@src/components/board/mutations/delete-folder";
import { useMoveRanklistItem } from "@src/components/board/mutations/move-item";
import {
	type NavbarBranchItem,
	fetchNavbarBranchQueryOptions,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { useAuthenticatedUser } from "@src/components/user/user-context";
import { NavbarSection } from "@src/gql/graphql";
import type { BranchData, MoveItemArgs, TreeItem } from "@src/lib/clean-tree";
import {
	captureNavbarFolderCreated,
	captureNavbarItemReordered,
} from "@src/utils/analytics";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Fuse from "fuse.js";
import { useCallback, useMemo } from "react";
import { useSearch } from "../context/search-context";
import { type NavTreeItem, toNavTreeItem } from "./nav-tree-item-model";

export type { NavTreeItem } from "./nav-tree-item-model";
export type {
	NavBoardTreeItem,
	NavCardTreeItem,
	NavFolderTreeItem,
} from "./nav-tree-item-model";

function filterNavTreeItemsByQuery(items: NavTreeItem[], query: string) {
	if (!query.trim()) {
		return items;
	}

	const fuse = new Fuse(items, {
		keys: ["name"],
		threshold: 0.3,
	});

	return fuse.search(query).map((item) => item.item);
}

function mapBranchItems(
	entries: NavbarBranchItem[] | undefined,
	section: NavbarSection,
	currentUserId: string,
	query: string,
	currentUserEmail?: string,
): NavTreeItem[] {
	if (!entries) {
		return [];
	}

	const items = entries
		.map((entry) =>
			toNavTreeItem(entry, section, currentUserId, currentUserEmail),
		)
		.filter((item): item is NavTreeItem => item !== null);

	return filterNavTreeItemsByQuery(items, query);
}

export function useNavBranchItems(
	branchId: string | null,
	section: NavbarSection = NavbarSection.MyWhiteboards,
): BranchData {
	const { query } = useSearch();
	const { id: currentUserId, email: currentUserEmail } = useAuthenticatedUser();
	const { data, isPending } = useQuery(
		fetchNavbarBranchQueryOptions(section, branchId),
	);

	return useMemo(
		() => ({
			items: mapBranchItems(
				data,
				section,
				currentUserId,
				query,
				currentUserEmail,
			),
			isLoading: isPending,
		}),
		[data, isPending, query, section, currentUserId, currentUserEmail],
	);
}

export function useRanklistTree(
	section: NavbarSection = NavbarSection.MyWhiteboards,
) {
	const queryClient = useQueryClient();
	const { query } = useSearch();
	const { id: currentUserId, email: currentUserEmail } = useAuthenticatedUser();

	const { mutate: moveItem } = useMoveRanklistItem();
	const { mutateAsync: createFolderMutation, isPending: isCreatingFolder } =
		useCreateFolder();
	const { mutateAsync: deleteFolderMutation } = useDeleteFolder();

	const handleMoveItem = useCallback(
		(args: MoveItemArgs) => {
			const { itemId, sourceBranchId, targetBranchId, targetIndex } = args;

			const targetItems =
				queryClient.getQueryData<NavbarBranchItem[]>(
					navbarBranchQueryKeys.branch(section, targetBranchId),
				) ?? [];

			// Match tree row indices (filtered by nav search) when resolving neighbors.
			const uiOrder = mapBranchItems(
				targetItems,
				section,
				currentUserId,
				query,
				currentUserEmail,
			);
			const afterKey =
				targetIndex > 0 ? uiOrder[targetIndex - 1]?.id : undefined;
			const beforeKey = uiOrder[targetIndex]?.id;

			captureNavbarItemReordered(section);
			moveItem({
				section,
				itemKey: itemId,
				parentKey: targetBranchId,
				sourceParentKey: sourceBranchId,
				targetIndex,
				beforeKey: beforeKey ?? null,
				afterKey: afterKey ?? null,
			});
		},
		[section, moveItem, queryClient, query, currentUserId, currentUserEmail],
	);

	const handleCreateFolder = useCallback(
		async (
			parentBranchId: string | null,
			folder: TreeItem & { name?: string },
		) => {
			const branchItems =
				queryClient.getQueryData<NavbarBranchItem[]>(
					navbarBranchQueryKeys.branch(section, parentBranchId),
				) ?? [];

			const firstItemKey = branchItems[0]?.key;

			const createdFolder = await createFolderMutation({
				section,
				name: folder.name ?? "New Folder",
				parentFolderKey: parentBranchId,
				beforeKey: firstItemKey ?? null,
			});

			if (createdFolder?.key) {
				captureNavbarFolderCreated(section);
			}

			return createdFolder?.key ?? null;
		},
		[createFolderMutation, section, queryClient],
	);

	const handleDeleteFolder = useCallback(
		async (folderId: string, _branchId: string | null) => {
			await deleteFolderMutation({
				section,
				key: folderId,
			});
		},
		[deleteFolderMutation, section],
	);

	return {
		handleMoveItem,
		handleCreateFolder,
		handleDeleteFolder,
		isCreatingFolder,
	};
}
